/**
 * Shared pricing utilities for the BV pricing UI.
 *
 * Catalog-driven pricing: each tier carries its own currency and amounts in
 * major units. Currency is determined by the user's country via the
 * getCountryInfo Cloud Function and is fixed per session — no user-side
 * currency toggle. Display formatting uses Intl.NumberFormat so JPY/NOK/BRL
 * etc. render with their proper symbol.
 */

export interface PricingTier {
  id: string;
  name: string;
  description?: string;
  currency: string; // ISO code: "USD", "EUR", "NOK", "JPY", ...
  monthly: number;  // major units, e.g., 9.99
  annually: number; // major units, e.g., 99.99
  playerLimit: number; // -1 for unlimited
  features: string[];
  featured?: boolean;
  iconPath?: string;
  offer?: number; // Angular legacy: maps to /config/plans[offer]. 0=KT, 1=HG, 2=Club, 5=Pro (3=CASINO and 4=ANTE are legacy entries).
}

export interface PricingConfig {
  tiers: PricingTier[];
  defaultInterval: string;
}

export interface SavingsCalculation {
  amount: number;
  formatted: string;
  percentage: number;
}

export class PricingUtils {
  /** Format an amount with proper currency symbol/code via Intl.NumberFormat. */
  static formatPrice(amount: number, currency: string, locale: string = "en"): string {
    try {
      const fractionDigits = ZERO_DECIMAL.has(currency) ? 0 : 2;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  static calculateSavings(monthly: number, annual: number, currency: string, locale: string = "en"): SavingsCalculation {
    const totalMonthly = monthly * 12;
    const savings = totalMonthly - annual;
    return {
      amount: savings,
      formatted: this.formatPrice(savings, currency, locale),
      percentage: Math.round((savings / totalMonthly) * 100),
    };
  }

  static getTierPrice(tier: PricingTier, interval: string): number {
    if (interval === "monthly" || interval === "month") return tier.monthly;
    return tier.annually;
  }

  static getFormattedTierPrice(tier: PricingTier, interval: string, locale: string = "en"): string {
    if (tier.monthly === 0 && tier.annually === 0) return ""; // caller renders "Free"
    return this.formatPrice(this.getTierPrice(tier, interval), tier.currency, locale);
  }

  static hasSavings(tier: PricingTier): boolean {
    return tier.monthly > 0 && tier.monthly * 12 > tier.annually;
  }

  static getTierSavings(tier: PricingTier, locale: string = "en"): SavingsCalculation | null {
    if (!this.hasSavings(tier)) return null;
    return this.calculateSavings(tier.monthly, tier.annually, tier.currency, locale);
  }

  static isValidInterval(interval: string): boolean {
    return ["monthly", "annually"].includes(interval);
  }

  /**
   * Default tier definitions in USD. The Angular wrapper overlays
   * catalog-derived prices+currency on top of these. If the catalog fetch
   * fails for any reason, these USD defaults render as a safe fallback.
   */
  static getDefaultConfig(currency: string = "USD"): PricingConfig {
    const tiers: PricingTier[] = [
      {
        id: "kitchen-table",
        name: "Kitchen Table",
        description: "FREE_PLAN_DESCRIPTION",
        currency,
        monthly: 0,
        annually: 0,
        playerLimit: 9,
        features: [
          "MAX_9",
          "ADMIN_REAL_TIME",
          "ADMIN_BLIND_STRUCTURE_CALCULATOR",
          "TOURNAMENT_CLOCK",
          "ADMIN_PAYOUT_CALCULATOR",
          "ADMIN_SEATING",
        ],
        featured: false,
        iconPath: "/assets/icons/heart.svg",
        offer: 0,
      },
      {
        id: "home-game",
        name: "Home Game",
        description: "ADMIN_EVERYTHING_IN_KITCHEN_TABLE",
        currency,
        monthly: 9.99,
        annually: 99.99,
        playerLimit: 27,
        features: [
          "MAX_27",
          "ADMIN_AD_FREE",
          "ADMIN_BOUNTY_TOURNAMENTS",
          "ADMIN_CUSTOM_BACKGROUND",
          "ADMIN_YOUR_LOGO",
          "ENHANCED_CHIPSET_MANAGEMENT",
        ],
        featured: false,
        iconPath: "/assets/icons/club.svg",
        offer: 1,
      },
      {
        id: "club",
        name: "Club",
        description: "ADMIN_EVERYTHING_HOME_GAME",
        currency,
        monthly: 17.99,
        annually: 179.99,
        playerLimit: -1,
        features: [
          "ADMIN_UNLIMITED_PLAYERS",
          "ADMIN_CREATE_LEAGUES",
          "ADMIN_DEFINE_LEAGUE_RULES",
          "ADMIN_AUTOMATIC_LEAGUE_RANKINGS",
        ],
        featured: true,
        iconPath: "/assets/icons/diamond.svg",
        offer: 2,
      },
      {
        id: "pro",
        name: "Pro",
        description: "ADMIN_EVERYTHING_CLUB",
        currency,
        monthly: 69.99,
        annually: 699.99,
        playerLimit: -1,
        features: [
          "PRO_PRIORITY_SUPPORT",
          "PRO_COMMERCIAL_LICENSE",
          "PRO_FOR_OPERATORS",
          "PRO_THERMAL_PRINTER_SEATS|beta",
        ],
        featured: false,
        iconPath: "/assets/icons/spade.svg",
        offer: 5,
      },
    ];
    return { tiers, defaultInterval: "monthly" };
  }
}

const ZERO_DECIMAL = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA",
  "PYG", "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);
